import type { KeyboardEvent } from "react";

import { BTStatusBadge } from "@btnet/core-components/BTStatusBadge";
import { BTText } from "@btnet/core-components/BTText";
import { cn } from "@btnet/utilities-core";

import { CurrencyDisplay } from "commonComponents/financial/CurrencyDisplay/CurrencyDisplay";

import { toneToDisplayType } from "../searchDialog.types";
import { CountPill } from "./CountPill";
import type { ISearchDialogRow } from "./searchDialogRow.types";

export interface ISearchDialogRowProps {
    row: ISearchDialogRow;
    isHighlighted: boolean;
    /** Set by the list when the row participates in combobox active-descendant. */
    elementId?: string;
    /**
     * Applied by the container, not the row — see {@link ISearchDialogRow}.
     * "option" inside the combobox listbox, where the input drives the keyboard;
     * "button" anywhere else, where nothing does and the row must take focus.
     */
    role?: "option" | "button";
    /** Moves the keyboard selection here, so pointer and keyboard agree. */
    onHover?: () => void;
    /**
     * From `xs` up, lay the row out in three columns, as in the AI Search
     * prototype: the kind (icon and label, or the Ask row's label) in a fixed
     * column, the title, then the subtitle at the right. Below `xs` the row
     * stacks as it does without this. Off where the row sits in a pane whose
     * width does not follow the viewport's (the Ask page's results).
     */
    columns?: boolean;
}

/**
 * One row in the dialog, in any section. Absent fields render as nothing rather
 * than a placeholder — a record with no amount is the normal case.
 */
export function SearchDialogRow({
    row,
    isHighlighted,
    elementId,
    role,
    onHover,
    columns = false,
}: ISearchDialogRowProps) {
    const { icon, title, subtitle, amount, status, trailingLabel, onSelect, count, leading } = row;
    const isInteractive = onSelect !== null;
    // An unroutable row outside a listbox stays inert rather than a dead button.
    const isButton = role === "button" && onSelect !== null;
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (onSelect !== null && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            // A held key opens the record once, not once per repeat.
            if (!event.repeat) {
                onSelect("keyboard");
            }
        }
    };
    // The record's kind leads the detail line ("Bill · Pine Hill"), so a list
    // mixing kinds names each one without a separate column.
    const detail = [trailingLabel, subtitle].filter((part) => part !== null).join(" · ");
    // Not `hidden xs:flex`: webforms CSS has a global `.hidden{display:none!important}`.
    const wideOnly = columns ? "max-xs:hidden" : "hidden";
    const narrowOnly = columns ? "xs:hidden" : undefined;
    const hasKind = icon !== null || trailingLabel !== null;

    return (
        <div
            id={elementId}
            role={role === "option" || isButton ? role : undefined}
            aria-selected={role === "option" ? isHighlighted : undefined}
            tabIndex={isButton ? 0 : undefined}
            // Not onSelect itself, which would be handed the click event.
            onClick={onSelect === null ? undefined : () => onSelect("click")}
            onKeyDown={isButton ? handleKeyDown : undefined}
            // mousemove, so the row under a stationary cursor reclaims the
            // selection after a keyboard move.
            onMouseMove={onHover}
            className={cn(
                "flex items-center gap-1 rounded-md px-3 py-2 max-md:px-2",
                columns && "xs:gap-6",
                isHighlighted && "bg-surface-selected",
                isInteractive ? "cursor-pointer" : "cursor-default",
                // Keep the selected fill under the pointer.
                isInteractive &&
                    (isHighlighted
                        ? "hover:bg-surface-selected-hover"
                        : "hover:bg-surface-default-hover"),
                isButton &&
                    "outline-none focus-visible:outline-solid focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-blue-50",
            )}
            data-testid="search-dialog-row"
            data-row-key={row.key}
            data-row-kind={row.kind}
        >
            {leading !== undefined && leading !== null && (
                <span
                    className={cn(
                        "flex shrink-0 items-center",
                        columns ? "w-20 xs:w-28" : "w-32 max-md:w-20",
                    )}
                >
                    {leading}
                </span>
            )}
            {/* The kind's own column, wide layout only. */}
            {columns && hasKind && (
                <span
                    className={cn(
                        "flex w-28 shrink-0 items-center gap-1 text-fg-secondary",
                        wideOnly,
                    )}
                    data-testid="search-dialog-row-kind"
                >
                    {icon !== null && (
                        <span className="flex shrink-0 [&>*]:size-4" aria-hidden="true">
                            {icon}
                        </span>
                    )}
                    {trailingLabel !== null && (
                        <BTText size="normal-md" tag="span" className="truncate">
                            {trailingLabel}
                        </BTText>
                    )}
                </span>
            )}
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <BTText
                    tag="span"
                    className="truncate text-lg font-normal text-fg-default max-md:text-base"
                >
                    {title}
                </BTText>
                {(icon !== null || detail.length > 0) && (
                    <span
                        className={cn(
                            "flex min-w-0 items-center gap-1 text-fg-secondary",
                            narrowOnly,
                        )}
                    >
                        {icon !== null && (
                            // Entity icons come sized for a column of their own;
                            // here one sits inline with the detail text.
                            <span className="flex shrink-0 [&>*]:size-4" aria-hidden="true">
                                {icon}
                            </span>
                        )}
                        {detail.length > 0 && (
                            <BTText size="normal-md" tag="span" className="truncate">
                                {detail}
                            </BTText>
                        )}
                    </span>
                )}
            </span>
            {/* The subtitle at the right, wide layout only. */}
            {columns && subtitle !== null && (
                <BTText
                    size="normal-md"
                    tag="span"
                    className={cn("max-w-1/2 shrink-0 truncate text-fg-secondary", wideOnly)}
                    testid="search-dialog-row-meta"
                >
                    {subtitle}
                </BTText>
            )}
            {amount !== null && (
                <BTText size="normal-sm" tag="span" className="shrink-0 text-fg-default">
                    {/* Never a hardcoded symbol: CurrencyDisplay reads the builder's
                        locale from context itself. Whole dollars — cents are
                        noise when scanning a list, and the record's own page
                        has the exact figure. */}
                    <CurrencyDisplay value={amount} decimalScale={0} />
                </BTText>
            )}
            {count !== undefined && count !== null && (
                <CountPill value={count.value} tone={count.tone} testid="search-dialog-row-count" />
            )}
            {status !== null && (
                <span className="shrink-0">
                    <BTStatusBadge
                        testid="search-dialog-row-status"
                        displayType={toneToDisplayType(status.tone)}
                        text={status.text}
                    />
                </span>
            )}
        </div>
    );
}
